import { Box, Center } from "@chakra-ui/layout"
import { observer } from "mobx-react"
import { useRouter } from "next/router"
import React from "react"

import { useSWRForGetAuthRegister } from "~/api/auth/register/get"
import { PostAuthRegisterBody } from "~/api/auth/register/post"
import Button from "~/components/atoms/Button"
import Icon from "~/components/atoms/Icon"
import TransitionLink from "~/components/atoms/TransitionLink"
import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import FeedbackMessage from "~/components/molecules/FeedbackMessage"
import { Color, SpacingSize } from "~/consts/style-mixins"
import { internalLink } from "~/services/helper"

import { HeaderForAuthRegister } from "./components/HeaderForAuthRegister"
import { Recaptcha } from "./components/Recaptcha"
import { RegisterLine } from "./sections/1-RegisterLine"
import { BasicInfo } from "./sections/2-BasicInfo"
import { useAuthRegisterPage } from "./useAuthRegisterPage"

const INFRA_INTERN_LOGIN_URL = `${process.env.INFRA_INTERN_URL}/auth/login`
const INFRA_USER_POLICY_URL = `${process.env.INFRA_INTERN_URL}/pages/user-policy`
const INFRA_PRIVACY_POLICY_URL = `${process.env.INFRA_INTERN_URL}/pages/privacy-policy`

export type AuthRegisterPageProps = {
  onSubmitForMock?: (data: PostAuthRegisterBody) => void
}

const AuthRegisterPage: React.VFC<AuthRegisterPageProps> = (props) => {
  const router = useRouter()
  const { data: formMasterData } = useSWRForGetAuthRegister()
  const {
    control,
    errorMessages,
    isLoading,
    isLoggedIn,
    isRecaptchaAuth,
    onBeforeSubmit,
    onSubmit,
    query,
    setIsRecaptchaAuth,
  } = useAuthRegisterPage(props)

  if (formMasterData === undefined || isLoggedIn === undefined) {
    return <LoadingPage />
  }

  // formMasterDataが取得できなかった場合TOPへ遷移させる
  if ("error" in formMasterData) {
    router.push(`${process.env.INFRA_INTERN_URL}/long-internship`)
    return <LoadingPage />
  }

  // ログイン中のアクセスをブロック
  if (isLoggedIn) {
    router.replace(internalLink("/profile"))
    return <LoadingPage />
  }

  return (
    <>
      <HeaderForAuthRegister />
      <Box bgColor={Color.BackgroundGray} pt={48} pb={32} as="form" autoComplete="off" onSubmit={onSubmit}>
        <Container size="Medium">
          <Box pt={10} pb={SpacingSize.M} textAlign="center">
            <Typography type="SubHero">新規登録</Typography>
            <TransitionLink href={INFRA_INTERN_LOGIN_URL} size="Small" icon="Next">
              アカウントをお持ちの方
            </TransitionLink>
          </Box>

          {query.login_provider_error ? (
            //  LINE連携に失敗した時
            <Box mt={SpacingSize.M}>
              <FeedbackMessage type="attention">{query.login_provider_error}</FeedbackMessage>
            </Box>
          ) : (
            //  LINE連携に成功した時
            query.nonce && (
              <Box mt={SpacingSize.M}>
                <FeedbackMessage type="primary">
                  連携完了しました。引き続き以下のフォームを入力して登録を完了しましょう。
                </FeedbackMessage>
              </Box>
            )
          )}

          {/* formのエラーがある時は表示 */}
          {errorMessages.length > 0 && (
            <Box mt={SpacingSize.M}>
              <FeedbackMessage type="attention" messages={errorMessages} />
            </Box>
          )}

          {/* LINE認証後は表示しない */}
          {!query.nonce && (
            <Box mt={SpacingSize.M}>
              <RegisterLine />
            </Box>
          )}

          <Box mt={SpacingSize.M}>
            <BasicInfo formMasterData={formMasterData} control={control} />
          </Box>
          <Center mt={SpacingSize.M}>
            <Box w={304}>
              <Recaptcha onChange={setIsRecaptchaAuth} />
              <Box mt={SpacingSize.M}>
                <Typography type="Label">
                  <TransitionLink size="Medium" href={INFRA_USER_POLICY_URL} target="_blank">
                    利用規約
                  </TransitionLink>
                  と
                  <TransitionLink size="Medium" href={INFRA_PRIVACY_POLICY_URL} target="_blank">
                    プライバシーポリシー
                  </TransitionLink>
                  に同意して
                </Typography>
              </Box>

              <Box mt={SpacingSize.M}>
                <Button
                  size="Medium"
                  onClick={onBeforeSubmit}
                  type="submit"
                  disabled={!isRecaptchaAuth}
                  isLoading={isLoading}
                >
                  新規登録して次へ
                </Button>
              </Box>
            </Box>
          </Center>
        </Container>
      </Box>
    </>
  )
}

const LoadingPage = () => (
  <>
    <HeaderForAuthRegister />
    <Center p={60}>
      <Icon type="Loading" size={16} color={Color.Primary} className="isLoading" />
    </Center>
  </>
)

export default observer(AuthRegisterPage)
